import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TeleworkerState } from '@app/core/models';
import { CoreService } from '@app/core/services';
import {
  AuthService,
  LoggerService,
  shareReplayRefCount,
} from '@adreno-common';
import { environment } from '@environments/environment';
import {
  BehaviorSubject,
  Observable,
  Subject,
  combineLatest,
  from,
} from 'rxjs';
import {
  distinctUntilKeyChanged,
  filter,
  map,
  shareReplay,
  startWith,
  switchMap,
  take,
  withLatestFrom,
} from 'rxjs/operators';
import {
  CloseOtherSessionsRequest,
  KillMonitorPayload,
  KillMonitorRequest,
  ListPresenceStatesVm,
  LogPresencePayload,
  LogPresenceRequest,
  PresenceDto,
  PresenceStateConstants,
  PresenceStateDto,
  SubmitRecordBase64Request,
  SubmitRecordRequest,
} from '../models';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { UnavailablePresenceStateComponent } from '@app/work/components';
import { RecordSubmitDto } from '@app/work/models';
import { MultiLoginComponent } from '../components';

@Injectable({
  providedIn: 'root',
})
export class PresenceService {
  private auxStates$: Observable<PresenceStateDto[]>;
  private connected$: Observable<HubConnectionState>;
  private forceLogout = new Subject<void>();
  private hubConn: HubConnection;
  private hubStateSub = new BehaviorSubject<HubConnectionState>(
    HubConnectionState.Disconnected
  );
  private isAuxStateSub = new BehaviorSubject<boolean>(false);
  private multiLoginModalRef?: NgbModalRef;
  private presenceSub = new BehaviorSubject<PresenceDto | undefined>(undefined);
  private retries = 0;
  private stateMapping: Map<TeleworkerState, PresenceStateConstants> = new Map([
    [TeleworkerState.Login, PresenceStateConstants.Login],
    [TeleworkerState.News, PresenceStateConstants.PortalMain],
    [TeleworkerState.Work, PresenceStateConstants.General],
    [TeleworkerState.Preview, PresenceStateConstants.Preview],
    [TeleworkerState.Tasks, PresenceStateConstants.Preview],
    [TeleworkerState.AvailableOB, PresenceStateConstants.AvailableOB],
    [TeleworkerState.AvailableIB, PresenceStateConstants.AvailableIB],
    [TeleworkerState.UnavailableOB, PresenceStateConstants.UnavailableOB],
    [TeleworkerState.UnavailableIB, PresenceStateConstants.UnavailableIB],
    [TeleworkerState.InboundConnected, PresenceStateConstants.TalkIB],
    [TeleworkerState.OutboundConnected, PresenceStateConstants.TalkOB],
    [TeleworkerState.WrapOB, PresenceStateConstants.WrapOB],
    [TeleworkerState.WrapIB, PresenceStateConstants.WrapIB],
    [TeleworkerState.HoldOB, PresenceStateConstants.HoldOB],
    [TeleworkerState.HoldIB, PresenceStateConstants.HoldIB],
    [TeleworkerState.TransferIB, PresenceStateConstants.TransferIB],
    [TeleworkerState.TransferOB, PresenceStateConstants.TransferOB],
    [TeleworkerState.CallDialling, PresenceStateConstants.Dialling],
    [TeleworkerState.UpdateDetails, PresenceStateConstants.UpdateDetails],
    [TeleworkerState.Reports, PresenceStateConstants.Reports],
    [TeleworkerState.Timesheets, PresenceStateConstants.Timesheets],
    [TeleworkerState.Training, PresenceStateConstants.Training],
    [TeleworkerState.DiallerLoggedIn, PresenceStateConstants.Telephony],
    [TeleworkerState.Standby, PresenceStateConstants.Standby],
    [TeleworkerState.Logout, PresenceStateConstants.Logout],
    [TeleworkerState.Scheduling, PresenceStateConstants.Scheduling],
    [TeleworkerState.IBMissedCall, PresenceStateConstants.IBMissedCall],
    [TeleworkerState.Ringing, PresenceStateConstants.Ringing],
    [TeleworkerState.Refresh, PresenceStateConstants.Refresh],
    [TeleworkerState.IBTransferOnly, PresenceStateConstants.IBTransferOnly],
    [TeleworkerState.Idle, PresenceStateConstants.Idle],
    [TeleworkerState.Voicemail, PresenceStateConstants.Voicemail],
  ]);
  private tenantApi = `${environment.apiPath}/tenants`;

  forceLogout$ = this.forceLogout.asObservable();
  hubState$ = this.hubStateSub.asObservable();
  isAuxState$ = this.isAuxStateSub.asObservable();

  presence$ = this.presenceSub.pipe(
    filter((presence) => !!presence),
    map((presence) => presence!),
    distinctUntilKeyChanged('stateId')
  );
  states$ = this.http
    .get<ListPresenceStatesVm>(`${this.tenantApi}/presencestates`)
    .pipe(
      startWith({ presenceStates: [] as PresenceStateDto[] }),
      map((vm) => vm.presenceStates),
      shareReplayRefCount()
    );

  constructor(
    authSvc: AuthService,
    private coreSvc: CoreService,
    private http: HttpClient,
    private modalSvc: NgbModal,
    private logger: LoggerService
  ) {
    this.connected$ = this.hubStateSub.pipe(
      filter((state) => state === HubConnectionState.Connected)
    );
    this.logPresence({
      stateId: PresenceStateConstants.Login,
      beginLocal: new Date(),
      campaignId: 0,
      queueId: 9,
      recordId: 0,
      customData: undefined,
    } as PresenceDto);

    this.auxStates$ = this.states$.pipe(
      map((states) =>
        states.filter((state: PresenceStateDto) => state.grouping === 'AUX')
      )
    );
    const retryTimes = [
      2000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000, 5000,
    ];
    this.hubConn = new HubConnectionBuilder()
      .withUrl(environment.presenceHub, {
        accessTokenFactory: () => authSvc.accessToken || '',
      })
      .withAutomaticReconnect({
        nextRetryDelayInMilliseconds: (context) => {
          this.logger.warn(
            'PresenceService',
            'hubConn.withAutomaticReconnect',
            context
          )();
          if (context.previousRetryCount < retryTimes.length) {
            return retryTimes[context.previousRetryCount];
          }
          return null;
        },
      })
      .configureLogging(LogLevel.Warning)
      .build();
    this.hubConn.onclose((error: Error | undefined) => {
      this.hubStateSub.next(this.hubConn.state);
      this.logger.error('PresenceService', 'hubConn.onclose', error)();
      this.restart();
    });

    this.hubConn.onreconnecting(() =>
      this.hubStateSub.next(this.hubConn.state)
    );
    this.hubConn.onreconnected(() => this.hubStateSub.next(this.hubConn.state));

    this.coreSvc.state$
      .pipe(
        filter((twState) => (twState ? this.stateMapping.has(twState) : false)),
        map((twState) =>
          twState ? this.stateMapping.get(twState) : undefined
        ),
        withLatestFrom(
          this.coreSvc.campaignId$,
          this.coreSvc.queueId$,
          this.coreSvc.recordId$,
          this.coreSvc.customData$,
          this.auxStates$
        )
      )
      .subscribe(
        ([stateId, campaignId, queueId, recordId, customData, auxStates]) => {
          const presence = {
            stateId,
            beginLocal: new Date(),
            campaignId,
            queueId,
            recordId,
            customData,
          } as PresenceDto;
          this.logger.debug('PresenceService', 'state change', presence);
          this.presenceSub.next(presence);

          this.isAuxStateSub.next(
            auxStates.filter((state) => state.id === stateId).length > 0
          );
        }
      );

    combineLatest([this.presence$, this.connected$]).subscribe(
      ([payload, connected]) => this.logPresence(payload)
    );

    authSvc.isLoggedIn$
      .pipe(
        filter((isLoggedIn) => !!isLoggedIn),
        take(1)
      )
      .subscribe(() => this.start());

    //window.addEventListener('beforeunload', this.beforeUnload);
  }

  /*
  private beforeUnload = (ev: BeforeUnloadEvent) => {
    this.logger.warn('PresenceService', 'beforeUnload')();
    this.logPresence({
      stateId: PresenceStateConstants.Refresh,
      beginLocal: new Date(),
      campaignId: 0,
      queueId: 9,
      recordId: 0,
      customData: undefined,
    } as PresenceDto);
    return true;
  };
  */

  private closeOtherSessions() {
    const request = new CloseOtherSessionsRequest();
    this.connected$.pipe(take(1)).subscribe(() => {
      this.logger.info('PresenceService', 'closeOtherSessions', request)();
      this.hubConn.send(request.name, request.payload);
    });
  }

  private logPresence(payload: LogPresencePayload): void {
    const request = new LogPresenceRequest(payload);
    this.connected$.pipe(take(1)).subscribe(() => {
      this.logger.info('PresenceService', 'logPresence', request)();
      this.hubConn.send(request.name, request.payload);
    });
  }

  private onLogout() {
    this.forceLogout.next();
    window.open('/assets/signed-out.html', '_self');
  }

  private onPrompt() {
    if (!this.multiLoginModalRef) {
      this.multiLoginModalRef = this.modalSvc.open(MultiLoginComponent, {
        size: 'md',
        backdrop: 'static',
        keyboard: false,
      });

      this.multiLoginModalRef.result.then(
        (confirm) => {
          this.multiLoginModalRef = undefined;
          if (confirm == 'keep') {
            this.closeOtherSessions();
          } else if (confirm == 'close') {
            this.onLogout();
          }
        },
        (dismiss) => {
          this.multiLoginModalRef = undefined;
          this.onLogout();
        }
      );
    }
  }

  private onUnprompt() {
    if (this.multiLoginModalRef) {
      this.multiLoginModalRef.close('unprompt');
    }
  }

  private restart(): void {
    this.retries++;
    if (this.retries < 20) {
      // for the initial retries - schedule for every 30 seconds
      setTimeout(() => this.start(), 30000);
    } else {
      // then wait a further 10 minutes before trying the process again.
      this.retries = 0;
      setTimeout(() => this.start(), 600000);
    }
  }

  private start(): void {
    this.hubConn
      .start()
      .then(() => {
        this.retries = 0;
        this.logger.debug(
          'PresenceService',
          'hubConn.start',
          this.hubConn.state
        )();
        this.hubConn.off('logout');
        this.hubConn.on('logout', () => this.onLogout());
        this.hubConn.off('prompt');
        this.hubConn.on('prompt', () => this.onPrompt());
        this.hubConn.off('unprompt');
        this.hubConn.on('unprompt', () => this.onUnprompt());
      })
      .catch((err) => {
        this.logger.error('PresenceService', 'hubConn.start', err)();
        this.restart();
      })
      .finally(() => {
        this.hubStateSub.next(this.hubConn.state);
      });
  }

  addStateMapping(twState: TeleworkerState, presenceState: number) {
    if (!this.stateMapping.has(twState)) {
      // add in the new mapping
      this.stateMapping.set(twState, presenceState);
    } else {
      // delete first
      this.stateMapping.delete(twState);
      // and add the new mapping in
      this.stateMapping.set(twState, presenceState);
    }
    this.coreSvc.state = twState;
  }
  killMonitor(payload: KillMonitorPayload): void {
    const request = new KillMonitorRequest(payload);
    this.connected$.pipe(take(1)).subscribe(() => {
      this.logger.info('PresenceService', 'killMonitor', request)();
      this.hubConn.send(request.name, request.payload);
    });
  }
  popStateSelectionModal() {
    this.auxStates$
      .pipe(take(1), withLatestFrom(this.coreSvc.state$))
      .subscribe(([auxStates, currentState]) => {
        let currPresenceState = this.stateMapping.get(currentState!);
        if (
          currPresenceState === undefined &&
          currentState === TeleworkerState.DiallerLogout
        )
          currPresenceState = PresenceStateConstants.UnavailableOB;

        if (
          auxStates.filter((state) => state.id === currPresenceState).length > 0
        ) {
          this.modalSvc.open(UnavailablePresenceStateComponent, {
            size: 'sm',
            backdrop: 'static',
            keyboard: false,
          });
        }
      });
  }

  submitRecord(record: RecordSubmitDto, base64: boolean = false) {
    return this.connected$.pipe(
      take(1),
      switchMap(() => {
        var request;
        if (base64) {
          const decodedString = JSON.stringify(record);
          const encodedString = btoa(decodedString);
          request = new SubmitRecordBase64Request(encodedString);
        } else {
          request = new SubmitRecordRequest(record);
        }
        this.logger.info('PresenceService', 'submitRecord', request)();
        return from(
          this.hubConn.invoke<boolean>(request.name, request.payload)
        );
      }),
      map((resp) => {
        if (!!resp) return true;
        throw new Error('Unable to submit record');
      })
    );
  }

  withinMessageLimit(record: RecordSubmitDto, base64: boolean = false) {
    var msg;
    if (base64) {
      msg = btoa(JSON.stringify(record));
    } else {
      msg = JSON.stringify(record);
    }
    return msg.length < messageSizeLimit - messageHeaderBuffer;
  }
}

const messageSizeLimit = 64 * 1024;
const messageHeaderBuffer = 1024;
